import * as React from 'react';
import { SuggestionCard } from '@brainfish-ai/components';
import type { SuggestionCardDiff, SuggestionCardPagination } from '@brainfish-ai/components';
import { useInstance } from '@milkdown/react';
import { editorViewCtx, parserCtx } from '@milkdown/kit/core';

import { isMilkdownContextNotFoundError } from '../../collaboration/constants';
import type { ReviewSuggestion } from '../../types';
import type { ParseProposedMd } from './decorations';
import type { ResolvedReviewSuggestionRange } from './types';
import {
  createApplyReviewSuggestionTransaction,
  createDismissReviewSuggestionTransaction,
  parseProposedMd,
  reviewSuggestionsDecoKey,
} from './plugin';
import { getNextReviewSuggestionId, orderReviewSuggestionsByResolvedRanges } from './selection';

export interface SuggestionCardRailProps {
  /**
   * Id of the currently selected suggestion.
   */
  selectedReviewSuggestionId: string | null | undefined;
  /**
   * Called when the selected suggestion changes.
   */
  onReviewSuggestionSelect: ((suggestionId: string | null) => void) | undefined;
  /**
   * Called after an accept transaction has been applied to the editor doc.
   */
  onReviewSuggestionAccept?: (suggestionId: string) => Promise<void> | void;
  /**
   * Called when the user rejects the selected suggestion.
   */
  onReviewSuggestionReject?: (suggestionId: string) => Promise<void> | void;
  onReviewSuggestionSourceClick?: (sourceId: string) => void;
  /**
   * The editor container ref. The rail card scans this subtree for the
   * selected suggestion decoration and uses it for vertical alignment.
   */
  editorRootRef: React.RefObject<HTMLElement>;
  /**
   * Whether the editor is read-only.
   */
  readOnly?: boolean;
}

/**
 * Reads the live DOM to reconstruct the original text a delete/replace
 * suggestion is pointing at. For deletes we concatenate the strikethrough
 * spans; for replaces we concatenate the `display: none` "hidden original"
 * spans stamped by the plugin's decoration builder.
 */
function readOriginalText(root: HTMLElement | null, suggestionId: string, opType: ReviewSuggestion['opType']): string {
  if (!root) return '';
  if (opType === 'insert') return '';

  const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(suggestionId) : suggestionId;
  const nodes = root.querySelectorAll<HTMLElement>(`[data-review-suggestion-id="${escapedId}"]`);

  const targetClass = opType === 'delete' ? 'target-delete' : 'target-replace-hidden';

  let text = '';
  nodes.forEach((node) => {
    if (node.classList.contains(targetClass)) {
      text += node.textContent ?? '';
    }
  });

  return text;
}

/**
 * Maps a suggestion's op type onto the `SuggestionCardDiff` the card expects:
 * - replace -> both sides
 * - delete -> old side only
 * - insert -> new side only
 */
function deriveDiff(suggestion: ReviewSuggestion, originalText: string): SuggestionCardDiff | undefined {
  switch (suggestion.opType) {
    case 'insert':
      return suggestion.proposedText ? { newText: suggestion.proposedText } : undefined;
    case 'delete':
      return originalText ? { oldText: originalText } : undefined;
    case 'replace':
      return originalText || suggestion.proposedText
        ? { oldText: originalText, newText: suggestion.proposedText }
        : undefined;
    default:
      return undefined;
  }
}

function derivePagination(
  suggestion: ReviewSuggestion,
  reviewSuggestions: ReviewSuggestion[],
  onReviewSuggestionSelect: ((suggestionId: string | null) => void) | undefined,
): SuggestionCardPagination | undefined {
  const index = reviewSuggestions.findIndex((item) => item.id === suggestion.id);

  if (index === -1) return undefined;
  const previousId = index > 0 ? reviewSuggestions[index - 1]?.id : undefined;
  const nextId = index < reviewSuggestions.length - 1 ? reviewSuggestions[index + 1]?.id : undefined;
  const selectSuggestion = onReviewSuggestionSelect;

  return {
    currentIndex: index + 1,
    totalCount: reviewSuggestions.length,
    onPrevious: previousId && selectSuggestion ? () => selectSuggestion(previousId) : undefined,
    onNext: nextId && selectSuggestion ? () => selectSuggestion(nextId) : undefined,
  };
}

/**
 * Right-rail suggestion card synced to the currently selected suggestion.
 */
export const SuggestionCardRail: React.FC<SuggestionCardRailProps> = ({
  selectedReviewSuggestionId,
  onReviewSuggestionSelect,
  onReviewSuggestionAccept,
  onReviewSuggestionReject,
  onReviewSuggestionSourceClick,
  editorRootRef,
  readOnly,
}) => {
  const [loadingInstance, getInstance] = useInstance();
  const [resolvedSuggestionIds, setResolvedSuggestionIds] = React.useState<Set<string>>(() => new Set());
  const pluginState = (() => {
    if (loadingInstance) return undefined;

    let state:
      | {
          ranges: ResolvedReviewSuggestionRange[];
          suggestions: ReviewSuggestion[];
        }
      | undefined;
    // The async review-suggestions loader gates editor-view creation on a network
    // fetch, so during teardown/recreate (readOnly toggle, room switch, StrictMode)
    // `getInstance()` can return an editor whose `editorViewCtx` slot is already gone.
    // Reading it then throws "Context editorView not found" during render. Swallow that
    // (matching useReviewSuggestionBulkActionsRef / useReviewSuggestionSelectionSync /
    // useEditorReady) and leave `state` undefined; the next render recovers.
    try {
      getInstance()?.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        const reviewSuggestionsState = reviewSuggestionsDecoKey.getState(view.state);
        if (!reviewSuggestionsState) return;
        state = {
          ranges: reviewSuggestionsState.ranges,
          suggestions: reviewSuggestionsState.suggestions,
        };
      });
    } catch (error) {
      if (!(error instanceof Error && error.message.includes('not found'))) throw error;
    }

    return state;
  })();
  const activeReviewSuggestions = React.useMemo(
    () => orderReviewSuggestionsByResolvedRanges(pluginState?.suggestions, resolvedSuggestionIds, pluginState?.ranges),
    [pluginState?.suggestions, pluginState?.ranges, resolvedSuggestionIds],
  );
  const suggestion = React.useMemo(
    () =>
      selectedReviewSuggestionId
        ? (activeReviewSuggestions.find((item) => item.id === selectedReviewSuggestionId) ?? null)
        : null,
    [activeReviewSuggestions, selectedReviewSuggestionId],
  );

  const [originalText, setOriginalText] = React.useState<string>('');

  React.useEffect(() => {
    if (!suggestion) {
      setOriginalText('');

      return undefined;
    }

    // ProseMirror mounts decoration DOM asynchronously relative to the
    // selection callback. We retry on next frame (and via MutationObserver)
    // until we find the node or the selection flips.
    let cancelled = false;

    const tryResolve = () => {
      if (cancelled) return false;
      const root = editorRootRef.current;
      const currentOriginalText = readOriginalText(root, suggestion.id, suggestion.opType);

      if (suggestion.opType !== 'insert' && !currentOriginalText) return false;

      setOriginalText((prev) => (prev === currentOriginalText ? prev : currentOriginalText));

      return true;
    };

    const raf = requestAnimationFrame(() => {
      tryResolve();
    });

    const root = editorRootRef.current;
    let observer: MutationObserver | null = null;
    if (root && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        if (tryResolve()) observer?.disconnect();
      });
      observer.observe(root, { subtree: true, childList: true, attributes: true });
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [suggestion, editorRootRef]);

  const details = suggestion?.details;
  const diff = suggestion ? deriveDiff(suggestion, originalText) : undefined;
  const pagination =
    suggestion && activeReviewSuggestions
      ? derivePagination(suggestion, activeReviewSuggestions, onReviewSuggestionSelect)
      : undefined;
  const [pendingAction, setPendingAction] = React.useState<'accept' | 'reject' | null>(null);

  const onAccept = React.useCallback(async () => {
    if (!suggestion || loadingInstance) return;
    setPendingAction('accept');
    try {
      const nextSuggestionId = getNextReviewSuggestionId(activeReviewSuggestions, suggestion.id);
      const editor = getInstance();
      let applied = false;
      try {
        editor?.action((ctx) => {
          const view = ctx.get(editorViewCtx);
          const parser = ctx.get(parserCtx);
          const parseMarkdown: ParseProposedMd = (md) => parseProposedMd(parser, md);
          const tr = createApplyReviewSuggestionTransaction(view.state, suggestion.id, parseMarkdown);
          if (!tr) return;
          view.dispatch(tr);
          applied = true;
        });
      } catch (error) {
        if (!isMilkdownContextNotFoundError(error)) throw error;
      }

      if (!applied) return;
      setResolvedSuggestionIds((ids) => new Set(ids).add(suggestion.id));
      await onReviewSuggestionAccept?.(suggestion.id);
      onReviewSuggestionSelect?.(nextSuggestionId);
    } finally {
      setPendingAction(null);
    }
  }, [
    activeReviewSuggestions,
    getInstance,
    loadingInstance,
    onReviewSuggestionAccept,
    onReviewSuggestionSelect,
    suggestion,
  ]);

  const onReject = React.useCallback(async () => {
    if (!suggestion || loadingInstance) return;
    setPendingAction('reject');
    try {
      const nextSuggestionId = getNextReviewSuggestionId(activeReviewSuggestions, suggestion.id);
      const editor = getInstance();
      let applied = false;
      try {
        editor?.action((ctx) => {
          const view = ctx.get(editorViewCtx);
          view.dispatch(createDismissReviewSuggestionTransaction(view.state, suggestion.id));
          applied = true;
        });
      } catch (error) {
        if (!isMilkdownContextNotFoundError(error)) throw error;
      }

      if (!applied) return;
      setResolvedSuggestionIds((ids) => new Set(ids).add(suggestion.id));
      await onReviewSuggestionReject?.(suggestion.id);
      onReviewSuggestionSelect?.(nextSuggestionId);
    } finally {
      setPendingAction(null);
    }
  }, [
    activeReviewSuggestions,
    getInstance,
    loadingInstance,
    onReviewSuggestionReject,
    onReviewSuggestionSelect,
    suggestion,
  ]);

  if (!suggestion) return null;

  const sourceId = details?.sourceId;

  return (
    <div className="max-h-[calc(100vh-1rem)]">
      <SuggestionCard
        source={details?.source}
        diff={diff}
        reason={details?.reason}
        pagination={pagination}
        className="w-full"
        readOnly={readOnly}
        onAccept={onReviewSuggestionAccept ? onAccept : undefined}
        onReject={onReviewSuggestionReject ? onReject : undefined}
        onSourceClick={
          sourceId && onReviewSuggestionSourceClick ? () => onReviewSuggestionSourceClick(sourceId) : undefined
        }
        acceptDisabled={pendingAction !== null || loadingInstance}
        rejectDisabled={pendingAction !== null}
      />
    </div>
  );
};
